/**
 * Converts a pixel value to its rem equivalent, assuming a 16px root font size.
 *
 * @param {number} pixels - The pixel value to convert.
 * @returns {number} The equivalent value in rem units.
 */
export const convertPixelsToRem = (pixels) => Number((pixels / 16).toFixed(4));

/**
 * Generates a responsive CSS clamp() value based on provided min/max pixel sizes
 * and viewport range.
 *
 * clamp() in CSS allows a value to fluidly scale between a minimum and maximum
 * based on the viewport width, without relying on discrete breakpoints.
 *
 * Reference:
 * https://www.marcbacon.com/understanding-clamp/
 *
 * @param {number} minPixelValue - The minimum size in pixels, applied at the smallest viewport.
 * @param {number} maxPixelValue - The maximum size in pixels, applied at the largest viewport.
 * @param {number} minViewportWidthPx - The viewport width (px) where scaling starts (default: 360).
 * @param {number} maxViewportWidthPx - The viewport width (px) where scaling stops (default: 1440).
 *
 * Math behind the scenes:
 * slope     = (maxSize - minSize) / (maxViewport - minViewport)
 * intercept = minSize - slope * minViewport
 *
 * Resulting CSS:
 * clamp(minRem, calc(interceptRem + slope * vw), maxRem)
 *
 * Intended for length-based CSS properties such as:
 * font-size, padding, margin, gap, width, height.
 */
export const clamp = (
  minPixelValue,
  maxPixelValue,
  minViewportWidthPx = 360,
  maxViewportWidthPx = 1440
) => {
  const slope = (maxPixelValue - minPixelValue) / (maxViewportWidthPx - minViewportWidthPx);

  const intercept = minPixelValue - slope * minViewportWidthPx;

  return `clamp(${convertPixelsToRem(minPixelValue)}rem, calc(${convertPixelsToRem(
    intercept
  )}rem + ${(slope * 100).toFixed(4)}vw), ${convertPixelsToRem(maxPixelValue)}rem)`;
};
